import React, { Fragment, useState } from "react"

import { Divider, ListItem, Drawer as MuiDrawer, Typography } from "@mui/material"
import T from "prop-types"
import { toPairs } from "ramda"
import { cx, isNilOrEmpty, mapIndexed as map } from "ramda-extension"

import { useTranslations } from "@cirrus/core/i18n/provider"

import { useDynamicStyles, useStyles } from "./css"
import { MuiIcon } from "../../atoms"
import { IconButton } from "../../molecules"

const needExpand = (key, value) => {
  if (value.length < 4) return false

  return `${key}${value}`.length > 36
}

export const Drawer = ({
  anchor = "left",
  className,
  container,
  content,
  height,
  onClose,
  open,
  tableInfo,
  title,
  variant = "persistent",
  width,
}) => {
  const { i18n } = useTranslations()
  const {
    drawer,
    drawerContent,
    drawerPaper,
    drawerTitle,
    expandableContainer,
    header,
    icon,
    listSection,
    listSectionHeader,
    listText,
    listTextLeft,
    listTextRight,
    notBoldText,
    paperAnchorDockedRight,
    pointerCursor,
    root,
  } = useStyles()

  const dynamicClasses = useDynamicStyles({ height, width })

  const NormalItem = ({ keyValue, notBold, value, asHTML, isHidden }) => {
    if (isHidden) return null
    return (
      <>
        <div className={listText}>
          <div className={listTextLeft}>{keyValue}</div>
          {!asHTML && <div className={cx(listTextRight, notBold && notBoldText)}>{value}</div>}
          {asHTML && <span dangerouslySetInnerHTML={{ __html: value }} />}
        </div>
        <Divider />
      </>
    )
  }

  const ExpandableItem = ({ asHTML, keyValue, notBold, value, isHidden }) => {
    const [expanded, setExpanded] = useState(false)
    const { expandableDinamycStyles } = useDynamicStyles({ expanded })

    const toggleExpand = () => setExpanded(!expanded)

    if (isHidden) return null

    /* dangerouslySetInnerHTML allow to use tags inside description (<br /> as example). be sure it secure */

    return (
      <>
        <div className={cx(listText, expandableContainer, expandableDinamycStyles)}>
          <span className={cx(listTextRight, notBold && notBoldText)}>
            <span onClick={toggleExpand} className={cx(listTextLeft, pointerCursor)}>
              {keyValue}
              {expanded ? (
                <MuiIcon.KeyboardArrowUp fontSize="small" />
              ) : (
                <MuiIcon.KeyboardArrowDown fontSize="small" />
              )}
            </span>
            {asHTML ? <span dangerouslySetInnerHTML={{ __html: value }} /> : <span>{value}</span>}
          </span>
        </div>
        <Divider />
      </>
    )
  }

  const getTableColumnList = () => (
    <Fragment>
      <div className={header}>
        <div className={icon}>
          <IconButton muiName="Close" size={"14"} onClick={onClose} />
        </div>

        <div className={drawerTitle}>{tableInfo.title}</div>
      </div>

      {!isNilOrEmpty(tableInfo) && (
        <div className={drawerContent}>
          <Fragment key={1}>
            {!isNilOrEmpty(tableInfo.overview) && (
              <Fragment>
                <ListItem disableGutters={true}>
                  <div className={listSectionHeader}>{i18n("Reports.Tabs:Overview")}</div>
                </ListItem>
                <div className={listSection}>
                  {map(
                    ([key, field], ind) => (
                      <Fragment key={`${ind}_${key}_${field.value}`}>
                        {field.expandable && needExpand(key, field.value) ? (
                          <ExpandableItem
                            keyValue={key}
                            value={field.value}
                            notBold={field.notBold}
                            asHTML={field.asHTML}
                            isHidden={field.isHidden}
                          />
                        ) : (
                          <NormalItem
                            keyValue={key}
                            value={field.value}
                            notBold={field.notBold}
                            asHTML={field.asHTML}
                            isHidden={field.isHidden}
                          />
                        )}
                      </Fragment>
                    ),
                    toPairs(tableInfo.overview),
                  )}
                </div>
              </Fragment>
            )}

            {!isNilOrEmpty(tableInfo.details) && (
              <Fragment>
                <ListItem disableGutters={true}>
                  <div className={listSectionHeader}>
                    {i18n("ScheduleItem.Sidebar.Sections:Details")}
                  </div>
                </ListItem>
                {map(
                  ([key, field], ind) => (
                    <Fragment key={`${ind}_${key}_${field.value}`}>
                      {field.expandable && needExpand(key, field.value) ? (
                        <ExpandableItem
                          keyValue={key}
                          value={field.value}
                          notBold={field.notBold}
                          asHTML={field.asHTML}
                          isHidden={field.isHidden}
                        />
                      ) : (
                        <NormalItem
                          keyValue={key}
                          value={field.value}
                          notBold={field.notBold}
                          asHTML={field.asHTML}
                          isHidden={field.isHidden}
                        />
                      )}
                    </Fragment>
                  ),
                  toPairs(tableInfo.details),
                )}
              </Fragment>
            )}
          </Fragment>
        </div>
      )}
    </Fragment>
  )

  const listContent = (
    <Fragment>
      <div className={header}>
        <div className={icon}>
          <IconButton name="cross" size={14} onClick={onClose} />
        </div>

        <Typography className={drawerTitle}>{title}</Typography>
      </div>

      <div className={drawerContent}>{content}</div>
    </Fragment>
  )

  return (
    <div className={root}>
      <MuiDrawer
        className={cx(drawer, dynamicClasses.drawer, className)}
        classes={{
          paper: cx(drawerPaper, dynamicClasses.drawerPaper),
          paperAnchorDockedRight,
        }}
        PaperProps={container ? { style: { position: "absolute" } } : {}}
        ModalProps={{ container, disablePortal: true, keepMounted: true }}
        {...{ anchor, onClose, open, variant }}>
        {!isNilOrEmpty(tableInfo) ? getTableColumnList() : listContent}
      </MuiDrawer>
    </div>
  )
}

Drawer.propTypes = {
  anchor: T.oneOf(["bottom", "left", "right", "top"]),
  anchorRef: T.oneOfType([T.instanceOf(Element), T.object, T.func]),
  className: T.string,
  content: T.oneOfType([T.element, T.node]),
  height: T.oneOfType([T.number, T.string]),
  onClose: T.func,
  title: T.string,
  variant: T.oneOf(["temporary", "persistent", "permanent"]),
  width: T.oneOfType([T.number, T.string]),
}
